import type { Locale } from "date-fns";
import { el, enUS } from "date-fns/locale";

/**
 * Central language registry for the order app (and shared helpers).
 *
 * To add a language (e.g. `de`):
 * 1. Add `de` to `SUPPORTED_LANGUAGE_CODES` below.
 * 2. Add `de` to `SUPPORTED_LANGUAGES` (index order = legacy `useLang("a|b|c")` pipe segment order).
 * 3. Extend `LANGUAGE_LOCALES` (`intl` tags, `dateFns`, `adminDatePicker`), `LANGUAGE_OPTIONS`, `TERMS_PATHS.byLang`, `LANG_ASSETS`.
 * 4. Add `de` to every `*-strings.ts` object; weekdays/months use helpers in `dates.ts`.
 * 5. Provide RTDB/admin `Lang` copy and static HTML under `public/terms/`.
 */

/** Supported app language codes; extend when adding a language. */
export const SUPPORTED_LANGUAGE_CODES = {
    el: true,
    en: true,
} as const;

export type Language = keyof typeof SUPPORTED_LANGUAGE_CODES;

/** Fallback when settings/RTDB hold a missing or unsupported code. */
export const DEFAULT_LANGUAGE = 'en' satisfies Language;

/** Type guard for supported language codes (e.g. callable payload validation). */
export function isLanguage(raw: unknown): raw is Language {
    return typeof raw === 'string' && raw in SUPPORTED_LANGUAGE_CODES;
}

/** Coerce RTDB / query / unknown values to a supported {@link Language}. */
export function languageFromValue(raw: unknown): Language {
    return isLanguage(raw) ? raw : DEFAULT_LANGUAGE;
}

/** Localized RTDB/admin fields — one optional string per {@link Language}. */
export type Lang = Partial<Record<Language, string>>;

/** Insertion order = index for legacy `useLang("el|en|…")` pipe strings. */
export const SUPPORTED_LANGUAGES = {
    el: 0,
    en: 1,
} as const satisfies Record<Language, number>;

export const LANGUAGES = Object.keys(SUPPORTED_LANGUAGES) as Language[];

export type LocaleUsage = "date" | "dateTime" | "number";

export type LanguageLocales = Record<LocaleUsage, string> & {
    dateFns: Locale;
    /** MUI `DatePicker` `format` (date-fns tokens); align with admin list/export date order per locale. */
    adminDatePicker: string;
};

export const LANGUAGE_LOCALES: Record<Language, LanguageLocales> = {
    el: { date: "el-GR", dateTime: "el", number: "el-GR", dateFns: el, adminDatePicker: "dd/MM/yyyy" },
    en: { date: "en-US", dateTime: "en-GB", number: "en-GB", dateFns: enUS, adminDatePicker: "MM/dd/yyyy" },
};

/** Display metadata per app language; add an entry when extending `SUPPORTED_LANGUAGES`. */
export const LANGUAGE_OPTIONS: Record<Language, { nativeName: string; flagIcon: string; shortLabel: string }> = {
    el: { nativeName: "Ελληνικά", flagIcon: "/img-assets/lang-el.svg", shortLabel: "Ελ" },
    en: { nativeName: "English", flagIcon: "/img-assets/lang-en.svg", shortLabel: "En" },
};

export type TermsDoc = "terms" | "privacy" | "cookies" | "instructions" | "termsPackages";

const TERMS_PATHS: Record<TermsDoc, { default: string; byLang: Partial<Record<Language, string>> }> = {
    terms: { default: "/terms/terms.html", byLang: { el: "/terms/terms-el.html", en: "/terms/terms-en.html" } },
    privacy: { default: "/terms/privacy.html", byLang: { el: "/terms/privacy-el.html", en: "/terms/privacy-en.html" } },
    cookies: { default: "/terms/cookies.html", byLang: { el: "/terms/cookies-el.html", en: "/terms/cookies-en.html" } },
    instructions: { default: "/instructions.html", byLang: { el: "/instructions-el.html", en: "/instructions-en.html" } },
    termsPackages: { default: "/terms/terms-packages.html", byLang: { el: "/terms/terms-packages-el.html", en: "/terms/terms-packages-en.html" } },
};

/** Localized static HTML under `public/`; falls back to default page when no `-{lang}` file is mapped. */
export function termsUrl(doc: TermsDoc, lang: Language): string {
    return TERMS_PATHS[doc].byLang[lang] ?? TERMS_PATHS[doc].default;
}

/** Per-language static assets; add keys when a new language needs its own file. */
export const LANG_ASSETS = {
    gefsinusLogoDark: {
        el: "/img-assets/logo-gefsinus-dark-el.png",
        en: "/img-assets/logo-gefsinus-dark-en.png",
    },
} as const satisfies Record<string, Partial<Record<Language, string>>>;

export function langAsset<K extends keyof typeof LANG_ASSETS>(key: K, lang: Language): string {
    const map = LANG_ASSETS[key];
    return map[lang] ?? map.en ?? map.el ?? "";
}

export function langPipeIndex(lang: Language): number {
    return SUPPORTED_LANGUAGES[lang];
}

/** BCP-47 locale for `Intl` / `toLocaleString` (shared by app UI and `functions/src/api/dates.ts`). */
export function localeFromLang(lang: Language, usage: LocaleUsage = "date"): string {
    return LANGUAGE_LOCALES[lang][usage];
}

/** date-fns `format` locale for this app language (see `LANGUAGE_LOCALES[lang].dateFns`). */
export function dateFnsLocaleFromLang(lang: Language): Locale {
    return LANGUAGE_LOCALES[lang].dateFns;
}

/** MUI admin `DatePicker` format string (see `LANGUAGE_LOCALES[lang].adminDatePicker`). */
export function adminDatePickerFormatFromLang(lang: Language): string {
    return LANGUAGE_LOCALES[lang].adminDatePicker;
}

/**
 * Greek uppercase without tonos (accent).
 * NFD normalization decomposes accents; stripping U+0300–036F removes them; then uppercase.
 */
export function formatGreekUpper(value: string): string {
    return value
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toUpperCase();
}

/** Locale-aware uppercase: Greek strips tonos before capitals; other languages use `toUpperCase`. */
export function uppercaseForLang(value: string, lang: Language | string): string {
    return languageFromValue(lang) === "el" ? formatGreekUpper(value) : value.toUpperCase();
}
